/**
 * The front door, for somebody who has not signed in.
 *
 * Everything here is unauthenticated, which is the constraint that shapes it:
 * no document titles, no counts, no thread history, nothing drawn from the
 * library. What it can honestly show is how the thing works and what it
 * guarantees — and those are the two questions a Network Control engineer
 * being asked to trust an AI answer actually has.
 *
 * No invented numbers. No adoption figures, no accuracy percentages, no
 * testimonials. Every claim below is a property of the system that can be
 * checked in the code, and a landing page for a records system is the last
 * place to start making things up.
 */

import { Link } from "react-router-dom";

function Mark({ paths }: { paths: string[] }) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}

const HOW = [
  {
    title: "It searches, twice",
    body: "A keyword search for the exact terms — a document number, a point name — and a vector search for meaning, so “gas leak” also finds “escape”. The two ranked lists are fused into one.",
    paths: ["M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14", "m16.2 16.2 4 4"],
  },
  {
    title: "It cites every claim",
    body: "Each statement carries a marker to the passage it was read from, and the source panel names the document, the version and the page. A citation that does not resolve costs the answer a regeneration, then a refusal.",
    paths: ["M6 3h9l4 4v14H6z", "M15 3v4h4", "M9 12h7", "M9 16h5"],
  },
  {
    title: "It declines",
    body: "When the compendium does not cover a question, the assistant says so instead of answering from general knowledge. A refusal is the correct outcome, not a failure — and the one thing a grounded system must be able to do.",
    paths: ["M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18", "m8.5 8.5 7 7"],
  },
];

const GUARANTEES = [
  {
    title: "Permissions apply at retrieval",
    body: "A document you may not open is excluded before the model sees it, not filtered out of the results afterwards. There is no path by which an answer is grounded in something you could not have read yourself.",
  },
  {
    title: "SharePoint stays the system of record",
    body: "This indexes and routes; it does not replace the library. Published metadata is written back to the source, and retention labels are read from it rather than reimplemented here.",
  },
  {
    title: "Every question is on the record",
    body: "Who asked, what was asked, what was answered, which documents it drew on, and whether it declined — kept for seven years and readable on the audit screen.",
  },
  {
    title: "Review is by name",
    body: "An author chooses the reviewers and the approver. The document stays in review until every one of them has answered, and a change request sends it back with a reason attached.",
  },
];

export function LandingScreen() {
  return (
    <div className="min-h-screen bg-ink text-pearl">
      {/* ------------------------------------------------------------- nav */}
      <header className="mx-auto flex max-w-[1120px] items-center gap-4 px-6 py-6 lg:px-10">
        <img
          src="/assets/nxzen-logomark.jpg"
          alt=""
          className="h-8 w-8 rounded-chip object-cover"
        />
        <span className="text-md+ font-bold">SGN Compendium DMS</span>
        <span className="ml-auto" />
        <Link
          to="/login"
          className="rounded-field border border-[#26262B] px-[15px] py-[9px] text-base font-bold text-pearl no-underline transition-colors hover:bg-[#141418]"
        >
          Sign in
        </Link>
      </header>

      {/* ----------------------------------------------------------- hero */}
      <section className="mx-auto grid max-w-[1120px] items-center gap-12 px-6 pb-16 pt-10 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 lg:pb-24 lg:pt-16">
        <div>
          <span className="inline-flex items-center gap-2 rounded-pill border border-[#26262B] px-3 py-1 text-sm font-bold text-muted-soft">
            <span className="h-[6px] w-[6px] rounded-full bg-accent" aria-hidden />
            Operational Technology
          </span>
          <h1 className="mt-5 font-display text-[42px] leading-[1.03] text-pearl lg:text-hero">
            Ask the compendium.
            <br />
            Get the <span className="text-accent">page it came from</span>.
          </h1>
          <p className="mt-6 max-w-lg text-md+ leading-[1.7] text-muted-soft">
            An AI-assisted document management system for SGN Operational
            Technology. Ask a question in plain English; every claim in the
            answer is cited to the document, the version and the page it was
            read from. If the compendium does not cover it, the assistant says
            so.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/login" className="btn-accent no-underline">
              Sign in to Compendium
            </Link>
            <a
              href="#how"
              className="rounded-field px-[14px] py-[9px] text-base font-bold text-muted-soft no-underline transition-colors hover:text-pearl"
            >
              How it works
            </a>
          </div>
        </div>

        {/* An illustration of the thing itself: a question, a cited answer,
            and the source underneath. Written for this page rather than taken
            from the corpus — a public page is no place for document text. */}
        <div className="rounded-panel border border-[#1E1E23] bg-[#0A0A0C] p-4 shadow-2xl">
          <div className="flex justify-end">
            <div className="max-w-[80%] rounded-[12px] bg-[#1A1A1E] px-[14px] py-[10px] text-base+ leading-[1.6] text-pearl">
              What has to be done before an end to end test is started?
            </div>
          </div>
          <div className="mt-3 rounded-[12px] border border-[#1E1E23] bg-[#111114] px-[14px] py-[12px]">
            <p className="text-base+ leading-[1.6] text-muted-soft">
              Before the test is started:
            </p>
            <ol className="mt-2 flex list-none flex-col gap-2">
              {[
                "Confirm the database and mimic configuration is complete",
                "Produce the latest version of the End to End report",
                "Agree the start with the control room",
              ].map((line, index) => (
                <li key={line} className="flex gap-[9px] text-base+ leading-[1.6]">
                  <span className="w-[15px] shrink-0 text-right font-extrabold tabular-nums text-accent">
                    {index + 1}.
                  </span>
                  <span className="flex-1 text-pearl">
                    {line}
                    <sup
                      className="ml-[3px] rounded-[4px] px-1 py-px align-super text-2xs font-extrabold"
                      style={{ background: "#25203A", color: "#C9B8F0" }}
                    >
                      1
                    </sup>
                  </span>
                </li>
              ))}
            </ol>
            <div className="mt-3 border-t border-[#1E1E23] pt-2.5">
              <div className="text-2xs font-extrabold uppercase tracking-[0.12em] text-muted-faint">
                Sources
              </div>
              <div className="mt-1.5 flex items-center gap-2 text-sm text-muted-soft">
                <span
                  className="rounded-[4px] px-1.5 py-px text-2xs font-extrabold"
                  style={{ background: "#25203A", color: "#C9B8F0" }}
                >
                  1
                </span>
                <span className="truncate">
                  Testing procedure · v4 · pages 6–7
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------- how it works */}
      <section id="how" className="border-t border-[#151518] bg-[#08080A]">
        <div className="mx-auto max-w-[1120px] px-6 py-16 lg:px-10 lg:py-20">
          <h2 className="font-display text-heroSub text-pearl">
            Three things it does, every time
          </h2>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {HOW.map((item) => (
              <div key={item.title}>
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-nav border border-[#26262B] text-accent">
                  <Mark paths={item.paths} />
                </span>
                <h3 className="mt-4 text-md+ font-bold text-pearl">{item.title}</h3>
                <p className="mt-2 text-base+ leading-[1.7] text-muted-soft">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------ guarantees */}
      <section className="border-t border-[#151518]">
        <div className="mx-auto max-w-[1120px] px-6 py-16 lg:px-10 lg:py-20">
          <h2 className="font-display text-heroSub text-pearl">
            Built for a system of record
          </h2>
          <p className="mt-3 max-w-xl text-base+ leading-[1.7] text-muted-soft">
            The compendium is what Network Control works from during an
            incident. That sets the bar for what an assistant sitting in front
            of it is allowed to do.
          </p>
          <div className="mt-10 grid gap-x-10 gap-y-9 md:grid-cols-2">
            {GUARANTEES.map((item) => (
              <div key={item.title} className="border-t border-[#1E1E23] pt-5">
                <h3 className="text-md+ font-bold text-pearl">{item.title}</h3>
                <p className="mt-2 text-base+ leading-[1.7] text-muted-soft">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- cta */}
      <section className="border-t border-[#151518] bg-[#08080A]">
        <div className="mx-auto flex max-w-[1120px] flex-wrap items-center gap-6 px-6 py-14 lg:px-10">
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-heroSub text-pearl">
              Sign in with your SGN account
            </h2>
            <p className="mt-2 text-base+ leading-[1.7] text-muted-soft">
              Roles come from Entra ID group membership. New starters default to
              System Engineer, which is read-only.
            </p>
          </div>
          <Link to="/login" className="btn-accent shrink-0 no-underline">
            Sign in
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#151518]">
        <div className="mx-auto max-w-[1120px] px-6 py-8 text-2xs text-muted-faint lg:px-10">
          Every answer cites its source · Permissions applied at retrieval, not
          after · Seven-year audit trail
        </div>
      </footer>
    </div>
  );
}
